import {
    ApiError,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    UserRelationTypesEnum,
    CompanyEmployeesService,
    IndividualSubordinatesService,
    PermissionInterface,
    CompanyEmployeeInterface,
    OwnerTradingAccountInterface,
    TradingAccountAccessInterface,
    IndividualSubordinateInterface,
    PermissionModel,
    CompanyEmployeeModel,
    IndividualSubordinateModel,
    SupervisedUsersCollection
} from '@spa/api';

import {Helper, Peer} from '../../../../shared/classes';

import {AssignSupervisedRightsResponseType} from '../../types';
import {PanelClientUsersEditResolverErrorStatesEnum} from '../../enums/error-states';

import {PanelClientUsersEditMessages} from '../../../../../assets/messages';

export class PanelClientUsersEditTrait {
    private constructor() {
    }

    static getInitialValidationErrorMessages(errors: ApiError[]): string[] {
        return errors.map((error: ApiError) => {
            switch (error.status) {
                case PanelClientUsersEditResolverErrorStatesEnum.GetClientUsersFailed:
                    return PanelClientUsersEditMessages.GetClientUserFailed.message;
                case PanelClientUsersEditResolverErrorStatesEnum.GetClientUserNoData:
                    return PanelClientUsersEditMessages.GetClientUserNoData.message;
                case PanelClientUsersEditResolverErrorStatesEnum.GetClientFailed:
                    return PanelClientUsersEditMessages.GetClientFailed.message;
                case PanelClientUsersEditResolverErrorStatesEnum.GetClientNoData:
                    return PanelClientUsersEditMessages.GetClientNoData.message;
                case PanelClientUsersEditResolverErrorStatesEnum.GetCountriesFailed:
                    return PanelClientUsersEditMessages.GetCountriesFailed.message;
                case PanelClientUsersEditResolverErrorStatesEnum.GetCountriesInvalidData:
                    return PanelClientUsersEditMessages.GetCountriesInvalidData.message;
                default:
                    return PanelClientUsersEditMessages.UnknownError.message;
            }
        });
    }

    static getAccessRightsErrors(
        accountsResponse: ServerResponseData<OwnerTradingAccountInterface[]>,
        accessRightsResponse: ServerResponseData<TradingAccountAccessInterface[]>,
        permissionsResponse: ServerResponseData<PermissionInterface[]>
    ): string[] {
        const errors = [];

        if (accountsResponse.status !== HttpStatusCodesEnum.Ok) {
            errors.push(PanelClientUsersEditMessages.GetClientAccountsFailed.message);
        }

        if (Helper.errorsHelper().checkForInvalidRespondedArray<OwnerTradingAccountInterface>(accountsResponse)) {
            errors.push(PanelClientUsersEditMessages.GetClientAccountsNoData.message);
        }

        if (accessRightsResponse.status !== HttpStatusCodesEnum.Ok) {
            errors.push(PanelClientUsersEditMessages.GetClientUserAccountsRightsFailed.message);
        }

        if (permissionsResponse.status !== HttpStatusCodesEnum.Ok) {
            errors.push(PanelClientUsersEditMessages.GetPermissionsFailed.message);
        }

        if (Helper.errorsHelper().checkForInvalidRespondedArray<PermissionInterface>(permissionsResponse)) {
            errors.push(PanelClientUsersEditMessages.GetPermissionsNoData.message);
        }

        return errors;
    }

    static getClientUsersByAuthorityRequest(
        clientUserRelationType: number,
        authorityId: number,
        companyEmployeesService: CompanyEmployeesService,
        individualSubordinatesService: IndividualSubordinatesService
    ): ServerResponse<CompanyEmployeeInterface[] | IndividualSubordinateInterface[]> {
        switch (clientUserRelationType) {
            case UserRelationTypesEnum.CompanyEmployee:
                return companyEmployeesService.getAllByCompanyId(authorityId);
            case UserRelationTypesEnum.IndividualSubordinate:
                return individualSubordinatesService.getAllByIndividualId(authorityId);
            default:
                return Helper.errorsHelper().createBadRequestResponseObservable();
        }
    }

    static getSupervisedUsersRequest(
        clientUserRelationType: number,
        clientUserId: number,
        companyEmployeesService: CompanyEmployeesService,
        individualSubordinatesService: IndividualSubordinatesService
    ): ServerResponse<SupervisedUsersCollection> {
        switch (clientUserRelationType) {
            case UserRelationTypesEnum.CompanyEmployee:
                return companyEmployeesService.getSupervisedUsers(clientUserId);
            case UserRelationTypesEnum.IndividualSubordinate:
                return individualSubordinatesService.getSupervisedUsers(clientUserId);
            default:
                return Helper.errorsHelper().createBadRequestResponseObservable();
        }
    }

    static getClientUsersByAuthorityResponse(
        responses: AssignSupervisedRightsResponseType[]
    ): ServerResponseData<CompanyEmployeeInterface[] | IndividualSubordinateInterface[]> {
        return responses[0] as ServerResponseData<CompanyEmployeeInterface[] | IndividualSubordinateInterface[]>;
    }

    static getSupervisedRightsErrors(
        clientUsersResponse: ServerResponseData<CompanyEmployeeInterface[] | IndividualSubordinateInterface[]>,
        supervisedUsersResponse: ServerResponseData<SupervisedUsersCollection>,
        permissionsResponse: ServerResponseData<PermissionInterface[]>
    ): string[] {
        const errors = [];

        if (clientUsersResponse.status !== HttpStatusCodesEnum.Ok) {
            errors.push(PanelClientUsersEditMessages.GetClientUsersFailed.message);
        }

        const isDataInvalid = Helper.errorsHelper()
            .checkForInvalidRespondedArray<CompanyEmployeeInterface | IndividualSubordinateInterface>(clientUsersResponse);

        if (isDataInvalid) {
            errors.push(PanelClientUsersEditMessages.GetClientUsersNotFound.message);
        }

        if (supervisedUsersResponse.status !== HttpStatusCodesEnum.Ok) {
            errors.push(PanelClientUsersEditMessages.GetSupervisedUsersFailed.message);
        }

        if (permissionsResponse.status !== HttpStatusCodesEnum.Ok) {
            errors.push(PanelClientUsersEditMessages.GetPermissionsFailed.message);
        }

        if (Helper.errorsHelper().checkForInvalidRespondedArray<PermissionInterface>(permissionsResponse)) {
            errors.push(PanelClientUsersEditMessages.GetPermissionsNoData.message);
        }

        return errors;
    }

    static getClientUsersByUserRelation(
        userRelationType: number,
        response: ServerResponseData<CompanyEmployeeInterface[] | IndividualSubordinateInterface[]>
    ): CompanyEmployeeModel[] | IndividualSubordinateModel[] {
        switch (userRelationType) {
            case UserRelationTypesEnum.CompanyEmployee:
                return (response.data as CompanyEmployeeInterface[])
                    .map((item: CompanyEmployeeInterface) => new CompanyEmployeeModel(item)) as CompanyEmployeeModel[];
            case UserRelationTypesEnum.IndividualSubordinate:
                return (response.data as IndividualSubordinateInterface[])
                    .map((item: IndividualSubordinateInterface) => new IndividualSubordinateModel(item)) as IndividualSubordinateModel[];
            default:
                return [];
        }
    }

    static getSupervisedUsers(supervisedUsersResponse: ServerResponseData<SupervisedUsersCollection>): SupervisedUsersCollection {
        return !supervisedUsersResponse.data ? new SupervisedUsersCollection([]) :
            (supervisedUsersResponse.data as SupervisedUsersCollection);
    }

    static getNonSupervisedUsersPeers(
        clientUserRelationType: number,
        clientUsers: CompanyEmployeeModel[] | IndividualSubordinateModel[],
        supervisedUsersIds: number[]
    ): Peer[] {
        switch (clientUserRelationType) {
            case UserRelationTypesEnum.CompanyEmployee:
                return (clientUsers as CompanyEmployeeModel[]).filter((employee: CompanyEmployeeModel) => {
                    return supervisedUsersIds.indexOf(employee.id) < 0;
                }).map((employee: CompanyEmployeeModel) => new Peer(employee.id, employee.name));
            case UserRelationTypesEnum.IndividualSubordinate:
                return (clientUsers as IndividualSubordinateModel[]).filter((subordinate: IndividualSubordinateModel) => {
                    return supervisedUsersIds.indexOf(subordinate.id) < 0;
                }).map((subordinate: IndividualSubordinateModel) => new Peer(subordinate.id, subordinate.name));
            default:
                return [];
        }
    }

    static getPortalPermissions(response: ServerResponseData<PermissionInterface[]>): PermissionModel[] {
        return (response.data as PermissionInterface[]).map((item: PermissionInterface) => new PermissionModel(item));
    }
}
